import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertCircle,
  Calendar,
  Check,
  Clock,
  Download,
  Edit,
  FileSpreadsheet,
  History,
  Loader2,
  Plus,
  Search,
  Users,
  UserCheck,
  UserX,
  X
} from 'lucide-react';
import ScrollableTableContainer from '../common/ScrollableTableContainer';
import { buildApiUrl } from '../../utils/apiSetup';

const STATUSES = ['PENDING', 'PRESENT', 'ABSENT', 'HALF_DAY', 'LEAVE', 'HOLIDAY', 'WEEK_OFF'];

const statusClasses = {
  PENDING: 'bg-gray-100 text-gray-800 border-gray-200',
  PRESENT: 'bg-green-100 text-green-800 border-green-200',
  ABSENT: 'bg-red-100 text-red-800 border-red-200',
  HALF_DAY: 'bg-orange-100 text-orange-800 border-orange-200',
  LEAVE: 'bg-blue-100 text-blue-800 border-blue-200',
  HOLIDAY: 'bg-purple-100 text-purple-800 border-purple-200',
  WEEK_OFF: 'bg-slate-100 text-slate-800 border-slate-200'
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const monthIso = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const formatTime = (value) => {
  if (!value) return '-';
  const [hours = '0', minutes = '0'] = String(value).split(':');
  const date = new Date();
  date.setHours(Number(hours), Number(minutes), 0, 0);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatHours = (value) => (value === null || value === undefined ? '-' : Number(value).toFixed(2));

const toDbStatus = (value) => value === 'HALF_DAY' ? 'half_day' : value.toLowerCase();

const extractError = async (response, fallback) => {
  try {
    const data = await response.json();
    if (typeof data.detail === 'string') return data.detail;
    if (Array.isArray(data.detail) && data.detail.length > 0) return data.detail[0].msg || fallback;
  } catch {
    return fallback;
  }
  return fallback;
};

const downloadBlob = (content, filename, type) => {
  const blob = new Blob([content], { type });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const AttendanceModal = ({ isOpen, mode, employees, initialData, onClose, onSubmit, saving }) => {
  const [formData, setFormData] = useState({
    employee_id: '',
    attendance_date: todayIso(),
    check_in_time: '',
    check_out_time: '',
    status: 'PRESENT'
  });

  useEffect(() => {
    if (!isOpen) return;

    setFormData({
      employee_id: initialData?.employee_id || '',
      attendance_date: initialData?.attendance_date || todayIso(),
      check_in_time: initialData?.check_in_time ? String(initialData.check_in_time).slice(0, 5) : '',
      check_out_time: initialData?.check_out_time ? String(initialData.check_out_time).slice(0, 5) : '',
      status: initialData?.status || 'PRESENT'
    });
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  const isEdit = mode === 'edit';

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({
      ...formData,
      employee_id: Number(formData.employee_id),
      check_in_time: formData.check_in_time || null,
      check_out_time: formData.check_out_time || null
    });
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4"
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.96, opacity: 0 }}
          className="bg-white rounded-lg shadow-xl w-full max-w-xl"
        >
          <div className="flex items-center justify-between p-5 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{isEdit ? 'Edit Attendance' : 'Mark Attendance'}</h3>
              <p className="text-sm text-gray-500">Attendance time and status are saved against one employee per day.</p>
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
              <select
                value={formData.employee_id}
                onChange={(event) => setFormData(prev => ({ ...prev, employee_id: event.target.value }))}
                disabled={isEdit}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="">Select employee</option>
                {employees.map(employee => (
                  <option key={employee.id} value={employee.id}>
                    {employee.full_name} ({employee.emp_code})
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={formData.attendance_date}
                  onChange={(event) => setFormData(prev => ({ ...prev, attendance_date: event.target.value }))}
                  disabled={isEdit}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(event) => setFormData(prev => ({ ...prev, status: event.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {STATUSES.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Check-in Time</label>
                <input
                  type="time"
                  value={formData.check_in_time}
                  onChange={(event) => setFormData(prev => ({ ...prev, check_in_time: event.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Check-out Time</label>
                <input
                  type="time"
                  value={formData.check_out_time}
                  onChange={(event) => setFormData(prev => ({ ...prev, check_out_time: event.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 inline-flex items-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Save
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const HistoryModal = ({
  employee,
  history,
  loading,
  onClose,
  page,
  setPage,
  historyFilters,
  setHistoryFilters,
}) => {
  if (!employee) return null;

  const totalPages = Math.max(Math.ceil((history?.total || 0) / 10), 1);
  const summary = history?.summary;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4"
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.96, opacity: 0 }}
          className="bg-white rounded-lg shadow-xl w-full max-w-4xl"
        >
          <div className="flex items-center justify-between p-5 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{employee.employee_name}</h3>
              <p className="text-sm text-gray-500">{employee.employee_code} attendance history</p>
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                ['Present Days', summary?.present_days ?? 0],
                ['Leave Days', summary?.leave_days ?? 0],
                ['Absent Days', summary?.absent_days ?? 0],
                ['LWP Days', summary?.lwp_days ?? 0],
                ['Late Marks', summary?.late_marks ?? 0],
                ['Working Hours', Number(summary?.working_hours || 0).toFixed(1)],
              ].map(([label, value]) => (
                <div key={label} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs text-gray-500">{label}</p>
                  <p className="text-lg font-bold text-gray-900">{value}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                type="date"
                value={historyFilters.date_from}
                onChange={(e) => setHistoryFilters((prev) => ({ ...prev, date_from: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="From"
              />
              <input
                type="date"
                value={historyFilters.date_to}
                onChange={(e) => setHistoryFilters((prev) => ({ ...prev, date_to: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="To"
              />
              <input
                type="month"
                value={historyFilters.month}
                onChange={(e) => setHistoryFilters((prev) => ({ ...prev, month: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            {loading ? (
              <div className="h-32 flex items-center justify-center text-gray-500">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Loading history
              </div>
            ) : (
              <>
                <ScrollableTableContainer
                  header={null}
                  maxHeight={400}
                  showTopScrollbar={true}
                  stickyHeader={true}
                >
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Date</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Shift</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Expected In</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Actual In</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Check-out</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Hours</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Late Min</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {(history?.records || []).map(item => (
                        <tr key={item.id}>
                          <td className="px-4 py-3">{item.attendance_date}</td>
                          <td className="px-4 py-3">{item.shift_name || '—'}</td>
                          <td className="px-4 py-3">{formatTime(item.expected_check_in)}</td>
                          <td className="px-4 py-3">{formatTime(item.check_in_time)}</td>
                          <td className="px-4 py-3">{formatTime(item.check_out_time)}</td>
                          <td className="px-4 py-3">{formatHours(item.total_hours)}</td>
                          <td className="px-4 py-3">{item.late_flag ? item.late_by_minutes : 0}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full border text-xs font-medium ${statusClasses[item.status] || statusClasses.PRESENT}`}>
                              {item.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {(history?.records || []).length === 0 && (
                        <tr>
                          <td colSpan="8" className="px-4 py-8 text-center text-gray-500">No attendance history found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </ScrollableTableContainer>
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
                  <div className="flex gap-2">
                    <button onClick={() => setPage(Math.max(page - 1, 1))} disabled={page === 1} className="px-3 py-1 border rounded disabled:opacity-50">Previous</button>
                    <button onClick={() => setPage(Math.min(page + 1, totalPages))} disabled={page >= totalPages} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const CorrectionModal = ({ isOpen, record, onClose, onSubmit, saving }) => {
  const [form, setForm] = useState({
    request_type: 'missed_punch',
    reason: '',
    remarks: '',
    requested_status: '',
    requested_check_in: '',
    requested_check_out: '',
  });

  useEffect(() => {
    if (!isOpen || !record) return;
    setForm({
      request_type: 'missed_punch',
      reason: '',
      remarks: '',
      requested_status: record.status || '',
      requested_check_in: record.check_in_time ? String(record.check_in_time).slice(0, 5) : '',
      requested_check_out: record.check_out_time ? String(record.check_out_time).slice(0, 5) : '',
    });
  }, [isOpen, record]);

  if (!isOpen || !record) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-5 space-y-4">
        <h3 className="text-lg font-semibold">Request Attendance Correction</h3>
        <p className="text-sm text-gray-500">
          {record.employee_name} — {record.attendance_date}. Original status: {record.status}
        </p>
        <select
          value={form.request_type}
          onChange={(e) => setForm((p) => ({ ...p, request_type: e.target.value }))}
          className="w-full border rounded-lg px-3 py-2"
        >
          <option value="missed_punch">Missed punch</option>
          <option value="wrong_checkin">Wrong check-in</option>
          <option value="wrong_checkout">Wrong check-out</option>
          <option value="incorrect_absence">Incorrect absence</option>
          <option value="shift_correction">Shift correction</option>
        </select>
        <select
          value={form.requested_status}
          onChange={(e) => setForm((p) => ({ ...p, requested_status: e.target.value }))}
          className="w-full border rounded-lg px-3 py-2"
        >
          <option value="">Keep status</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <div className="grid grid-cols-2 gap-3">
          <input type="time" value={form.requested_check_in} onChange={(e) => setForm((p) => ({ ...p, requested_check_in: e.target.value }))} className="border rounded-lg px-3 py-2" />
          <input type="time" value={form.requested_check_out} onChange={(e) => setForm((p) => ({ ...p, requested_check_out: e.target.value }))} className="border rounded-lg px-3 py-2" />
        </div>
        <textarea
          value={form.reason}
          onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
          placeholder="Correction reason (required)"
          rows={3}
          className="w-full border rounded-lg px-3 py-2"
          required
        />
        <textarea
          value={form.remarks}
          onChange={(e) => setForm((p) => ({ ...p, remarks: e.target.value }))}
          placeholder="Additional remarks"
          rows={2}
          className="w-full border rounded-lg px-3 py-2"
        />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg">Cancel</button>
          <button
            type="button"
            disabled={saving || !form.reason.trim()}
            onClick={() => onSubmit({ ...form, attendance_id: record.id })}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg disabled:opacity-50"
          >
            Submit for Checker
          </button>
        </div>
      </div>
    </div>
  );
};

const AttendanceManagement = ({ workflowMode = 'admin' }) => {
  const { hasPermission } = useAuth();
  const isMakerMode = workflowMode === 'maker' || hasPermission('attendance:mark:dept') || hasPermission('attendance:mark:own');
  const isCheckerMode = workflowMode === 'checker' || hasPermission('attendance:approve:all') || hasPermission('attendance:approve:dept');
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({});
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [absentEmployees, setAbsentEmployees] = useState([]);
  const [lateEmployees, setLateEmployees] = useState([]);
  const [monthlySummary, setMonthlySummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sideLoading, setSideLoading] = useState(true);
  const [monthlyLoading, setMonthlyLoading] = useState(true);
  const { showSuccess, showError } = useNotification();
  const [saving, setSaving] = useState(false);
  const setError = (msg) => { if (msg) showError(msg); };
  const setSuccess = (msg) => { if (msg) showSuccess(msg); };

  const [filters, setFilters] = useState({
    search: '',
    department_id: '',
    attendance_status: '',
    attendance_date: todayIso()
  });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [summaryMonth, setSummaryMonth] = useState(monthIso());
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [historyEmployee, setHistoryEmployee] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyFilters, setHistoryFilters] = useState({ date_from: '', date_to: '', month: monthIso() });
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [correctionRecord, setCorrectionRecord] = useState(null);
  const [pendingCorrections, setPendingCorrections] = useState([]);
  const [correctionsLoading, setCorrectionsLoading] = useState(false);

  const headers = useMemo(() => ({
    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    'Content-Type': 'application/json'
  }), []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const loadReferenceData = useCallback(async () => {
    try {
      const [departmentResponse, employeeResponse] = await Promise.all([
        fetch(buildApiUrl('/api/v1/departments'), { headers }),
        fetch(buildApiUrl('/api/v1/benfish-employees/search?query=&limit=500'), { headers })
      ]);

      if (departmentResponse.ok) {
        setDepartments(await departmentResponse.json());
      }

      if (employeeResponse.ok) {
        const employeeData = await employeeResponse.json();
        setEmployees(employeeData.employees || []);
      }
    } catch {
      setError('Unable to load employee and department reference data.');
    }
  }, [headers]);

  const loadTodayAttendance = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const query = new URLSearchParams();
      query.set('attendance_date', filters.attendance_date);
      if (debouncedSearch.trim()) query.set('search', debouncedSearch.trim());
      if (filters.department_id) query.set('department_id', filters.department_id);
      if (filters.attendance_status) query.set('attendance_status', filters.attendance_status);

      const response = await fetch(buildApiUrl(`/api/v1/attendance/today/all?${query.toString()}`), { headers });
      if (!response.ok) throw new Error(await extractError(response, 'Failed to load attendance.'));

      const data = await response.json();
      setRecords(data.records || []);
      setSummary(data.summary || {});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [headers, filters.attendance_date, filters.department_id, filters.attendance_status, debouncedSearch]);

  const loadSideSections = useCallback(async () => {
    setSideLoading(true);
    try {
      const query = new URLSearchParams();
      query.set('attendance_date', filters.attendance_date);
      if (filters.department_id) query.set('department_id', filters.department_id);

      const [absentResponse, lateResponse] = await Promise.all([
        fetch(buildApiUrl(`/api/v1/attendance/absent?${query.toString()}`), { headers }),
        fetch(buildApiUrl(`/api/v1/attendance/late?${query.toString()}`), { headers })
      ]);

      if (absentResponse.ok) setAbsentEmployees(await absentResponse.json());
      if (lateResponse.ok) setLateEmployees(await lateResponse.json());
    } finally {
      setSideLoading(false);
    }
  }, [headers, filters.attendance_date, filters.department_id]);

  const loadMonthlySummary = useCallback(async () => {
    setMonthlyLoading(true);
    try {
      const [year, month] = summaryMonth.split('-');
      const query = new URLSearchParams({ year, month });
      if (filters.department_id) query.set('department_id', filters.department_id);

      const response = await fetch(buildApiUrl(`/api/v1/attendance/monthly-summary?${query.toString()}`), { headers });
      if (response.ok) {
        const data = await response.json();
        setMonthlySummary(data.records || []);
      }
    } finally {
      setMonthlyLoading(false);
    }
  }, [headers, filters.department_id, summaryMonth]);

  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);

  useEffect(() => {
    loadTodayAttendance();
  }, [loadTodayAttendance]);

  useEffect(() => {
    loadSideSections();
  }, [loadSideSections]);

  useEffect(() => {
    loadMonthlySummary();
  }, [loadMonthlySummary]);

  const openCreateModal = () => {
    setModalMode('create');
    setSelectedRecord(null);
    setShowModal(true);
  };

  const openEditModal = (record) => {
    setModalMode('edit');
    setSelectedRecord(record);
    setShowModal(true);
  };

  const handleSaveAttendance = async (formData) => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = modalMode === 'edit'
        ? await fetch(buildApiUrl(`/api/v1/attendance/${selectedRecord.id}`), {
            method: 'PUT',
            headers,
            body: JSON.stringify({
              check_in_time: formData.check_in_time,
              check_out_time: formData.check_out_time,
              status: toDbStatus(formData.status)
            })
          })
        : await fetch(buildApiUrl('/api/v1/attendance/manual'), {
            method: 'POST',
            headers,
            body: JSON.stringify(formData)
          });

      if (!response.ok) throw new Error(await extractError(response, 'Unable to save attendance.'));

      setSuccess(modalMode === 'edit' ? 'Attendance updated successfully.' : 'Attendance marked successfully.');
      setShowModal(false);
      await Promise.all([loadTodayAttendance(), loadSideSections(), loadMonthlySummary()]);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (record) => {
    setHistoryEmployee(record);
    setHistoryPage(1);
  };

  useEffect(() => {
    const loadHistory = async () => {
      if (!historyEmployee) return;
      setHistoryLoading(true);
      try {
        const query = new URLSearchParams({ page: historyPage, size: 10 });
        if (historyFilters.date_from) query.set('date_from', historyFilters.date_from);
        if (historyFilters.date_to) query.set('date_to', historyFilters.date_to);
        if (historyFilters.month) query.set('month', historyFilters.month);
        const response = await fetch(buildApiUrl(`/api/v1/attendance/employee/${historyEmployee.employee_id}?${query}`), { headers });
        if (response.ok) {
          setHistory(await response.json());
        }
      } finally {
        setHistoryLoading(false);
      }
    };

    loadHistory();
  }, [headers, historyEmployee, historyPage, historyFilters]);

  const exportRows = records.map(record => ({
    'Employee Name': record.employee_name,
    'Employee Code': record.employee_code,
    Department: record.department || '',
    Date: record.attendance_date,
    'Check In Time': formatTime(record.check_in_time),
    'Check Out Time': formatTime(record.check_out_time),
    'Total Hours': formatHours(record.total_hours),
    Status: record.status
  }));

  const exportCsv = () => {
    const headersRow = Object.keys(exportRows[0] || {
      'Employee Name': '', 'Employee Code': '', Department: '', Date: '', 'Check In Time': '', 'Check Out Time': '', 'Total Hours': '', Status: ''
    });
    const csv = [
      headersRow.join(','),
      ...exportRows.map(row => headersRow.map(key => `"${String(row[key] || '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    downloadBlob(csv, `attendance_${filters.attendance_date}.csv`, 'text/csv;charset=utf-8;');
  };

  const exportExcel = () => {
    const headersRow = Object.keys(exportRows[0] || {
      'Employee Name': '', 'Employee Code': '', Department: '', Date: '', 'Check In Time': '', 'Check Out Time': '', 'Total Hours': '', Status: ''
    });
    const tableRows = exportRows.map(row => `<tr>${headersRow.map(key => `<td>${row[key] || ''}</td>`).join('')}</tr>`).join('');
    const html = `<table><thead><tr>${headersRow.map(key => `<th>${key}</th>`).join('')}</tr></thead><tbody>${tableRows}</tbody></table>`;
    downloadBlob(html, `attendance_${filters.attendance_date}.xls`, 'application/vnd.ms-excel');
  };

  const summaryCards = [
    { label: 'Present', value: summary.present ?? summary.present_today ?? 0, icon: UserCheck, color: 'bg-green-600' },
    { label: 'Pending', value: summary.pending ?? summary.pending_today ?? 0, icon: Clock, color: 'bg-blue-600' },
    { label: 'Leave', value: summary.leave ?? summary.leave_today ?? 0, icon: Calendar, color: 'bg-indigo-600' },
    { label: 'Absent', value: summary.absent ?? summary.absent_today ?? 0, icon: UserX, color: 'bg-red-600' },
    { label: 'Late Employees', value: summary.late_employees ?? 0, icon: AlertCircle, color: 'bg-orange-600' },
  ];

  const loadPendingCorrections = useCallback(async () => {
    if (!isCheckerMode) return;
    setCorrectionsLoading(true);
    try {
      const response = await fetch(buildApiUrl('/api/v1/attendance/corrections/pending'), { headers });
      if (response.ok) {
        const data = await response.json();
        setPendingCorrections(data.items || []);
      }
    } finally {
      setCorrectionsLoading(false);
    }
  }, [headers, isCheckerMode]);

  useEffect(() => {
    loadPendingCorrections();
  }, [loadPendingCorrections, filters.attendance_date]);

  const submitCorrection = async (payload) => {
    setSaving(true);
    try {
      const response = await fetch(buildApiUrl('/api/v1/attendance/corrections'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          attendance_id: payload.attendance_id,
          request_type: payload.request_type,
          reason: payload.reason,
          remarks: payload.remarks,
          requested_status: payload.requested_status || null,
          requested_check_in: payload.requested_check_in || null,
          requested_check_out: payload.requested_check_out || null,
        }),
      });
      if (!response.ok) throw new Error(await extractError(response, 'Failed to submit correction.'));
      setSuccess('Correction submitted for checker approval.');
      setShowCorrectionModal(false);
      await loadTodayAttendance();
      if (isCheckerMode) await loadPendingCorrections();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCorrectionAction = async (correctionId, action) => {
    setSaving(true);
    try {
      const response = await fetch(buildApiUrl(`/api/v1/attendance/corrections/${correctionId}/${action}`), {
        method: 'POST',
        headers,
        body: JSON.stringify({ checker_remarks: action === 'approve' ? 'Approved' : 'Rejected' }),
      });
      if (!response.ok) throw new Error(await extractError(response, `Failed to ${action} correction.`));
      setSuccess(`Correction ${action}d.`);
      await Promise.all([loadTodayAttendance(), loadSideSections(), loadPendingCorrections()]);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openCorrectionModal = (record) => {
    setCorrectionRecord(record);
    setShowCorrectionModal(true);
  };

  return (
    <div className="max-w-full mx-auto space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Calendar className="w-6 h-6 mr-2 text-blue-600" />
            Attendance Management
          </h2>
          <p className="text-sm text-gray-600 mt-1">Daily attendance, manual corrections, employee history, and monthly summaries.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {hasPermission('attendance:export') && (
<button onClick={exportCsv} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 inline-flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export CSV
          </button>
)}
          {hasPermission('attendance:export') && (
<button onClick={exportExcel} className="px-4 py-2 border border-green-300 rounded-lg text-green-700 hover:bg-green-50 inline-flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4" />
            Export Excel
          </button>
)}
          {(hasPermission("attendance:mark:dept") || hasPermission("attendance:mark:own")) && (
<button onClick={openCreateModal} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center gap-2">
<Plus className="w-4 h-4" />
Mark Attendance
</button>
)}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        {summaryCards.map(card => (
          <div key={card.label} className="bg-white rounded-lg shadow border border-gray-100 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{card.label}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{card.value}</p>
              </div>
              <div className={`p-3 rounded-full ${card.color}`}>
                <card.icon className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow border border-gray-100 p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={filters.search}
              onChange={(event) => setFilters(prev => ({ ...prev, search: event.target.value }))}
              placeholder="Search name or employee code"
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={filters.department_id}
            onChange={(event) => setFilters(prev => ({ ...prev, department_id: event.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Departments</option>
            {departments.map(department => (
              <option key={department.id} value={department.id}>{department.name}</option>
            ))}
          </select>
          <select
            value={filters.attendance_status}
            onChange={(event) => setFilters(prev => ({ ...prev, attendance_status: event.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Statuses</option>
            {STATUSES.map(status => <option key={status} value={status}>{status}</option>)}
          </select>
          <input
            type="date"
            value={filters.attendance_date}
            onChange={(event) => setFilters(prev => ({ ...prev, attendance_date: event.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        <div className="xl:col-span-3 bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">Today's Attendance Table</h3>
              <p className="text-sm text-gray-500">{records.length} record{records.length === 1 ? '' : 's'} found</p>
            </div>
            {loading && <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />}
          </div>
          <ScrollableTableContainer
            header={null}
            maxHeight={500}
            showTopScrollbar={true}
            stickyHeader={true}
          >
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Employee Name</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Employee Code</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Department</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Check In Time</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Check Out Time</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Total Hours</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {records.map(record => (
                  <tr key={record.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <button onClick={() => openHistory(record)} className="font-medium text-blue-700 hover:text-blue-900">
                        {record.employee_name}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{record.employee_code}</td>
                    <td className="px-4 py-3 text-gray-700">{record.department || '-'}</td>
                    <td className="px-4 py-3 text-gray-700">{formatTime(record.check_in_time)}</td>
                    <td className="px-4 py-3 text-gray-700">{formatTime(record.check_out_time)}</td>
                    <td className="px-4 py-3 text-gray-700">{formatHours(record.total_hours)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full border text-xs font-medium ${statusClasses[record.status] || statusClasses.PRESENT}`}>
                        {record.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {isMakerMode && record.record_state !== 'locked' && (
                          <button
                            onClick={() => (record.record_state === 'draft' ? openEditModal(record) : openCorrectionModal(record))}
                            className="p-1 text-green-600 hover:text-green-800"
                            title={record.record_state === 'draft' ? 'Edit Attendance' : 'Request Correction'}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => openHistory(record)} className="p-1 text-blue-600 hover:text-blue-800" title="Attendance History">
                          <History className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && records.length === 0 && (
                  <tr>
                    <td colSpan="8" className="px-4 py-10 text-center text-gray-500">
                      <Users className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                      No attendance records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </ScrollableTableContainer>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow border border-gray-100">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Absent Employees</h3>
            </div>
            <div className="p-5 space-y-3 max-h-80 overflow-y-auto">
              {sideLoading ? (
                <div className="text-sm text-gray-500 flex items-center"><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading</div>
              ) : absentEmployees.length === 0 ? (
                <p className="text-sm text-gray-500">No absent employees (shift ended).</p>
              ) : absentEmployees.map(employee => (
                <div key={employee.employee_id} className="p-3 bg-red-50 border border-red-100 rounded-lg">
                  <p className="font-medium text-gray-900">{employee.employee_name}</p>
                  <p className="text-xs text-gray-600">{employee.employee_code} - {employee.department || 'No department'}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow border border-gray-100">
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Late Employees</h3>
            </div>
            <div className="p-5 space-y-3 max-h-80 overflow-y-auto">
              {sideLoading ? (
                <div className="text-sm text-gray-500 flex items-center"><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading</div>
              ) : lateEmployees.length === 0 ? (
                <p className="text-sm text-gray-500">No late check-ins.</p>
              ) : lateEmployees.map(employee => (
                <div key={`${employee.employee_id}-${employee.check_in_time}`} className="p-3 bg-orange-50 border border-orange-100 rounded-lg">
                  <p className="font-medium text-gray-900">{employee.employee_name}</p>
                  <p className="text-xs text-gray-600">{employee.department || 'No department'} - {formatTime(employee.check_in_time)}</p>
                  <p className="text-xs font-medium text-orange-700 mt-1">Late by {employee.late_by_minutes} minutes</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h3 className="font-semibold text-gray-900">Monthly Attendance Summary</h3>
            <p className="text-sm text-gray-500">Employee-wise present, absent, leave, late marks, and working hours</p>
          </div>
          <input
            type="month"
            value={summaryMonth}
            onChange={(event) => setSummaryMonth(event.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <ScrollableTableContainer
          header={null}
          maxHeight={400}
          showTopScrollbar={true}
          stickyHeader={true}
        >
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Employee</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Present Days</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Absent Days</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Leave Days</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Late Marks</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Working Hours</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {monthlyLoading ? (
                <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500">Loading monthly summary...</td></tr>
              ) : monthlySummary.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500">No monthly summary records found</td></tr>
              ) : monthlySummary.map(item => (
                <tr key={item.employee_id}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{item.employee_name}</p>
                    <p className="text-xs text-gray-500">{item.employee_code} - {item.department || 'No department'}</p>
                  </td>
                  <td className="px-4 py-3">{item.present_days}</td>
                  <td className="px-4 py-3">{item.absent_days}</td>
                  <td className="px-4 py-3">{item.leave_days}</td>
                  <td className="px-4 py-3">{item.late_marks}</td>
                  <td className="px-4 py-3">{formatHours(item.working_hours)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollableTableContainer>
      </div>

      <AttendanceModal
        isOpen={showModal}
        mode={modalMode}
        employees={employees}
        initialData={selectedRecord}
        onClose={() => setShowModal(false)}
        onSubmit={handleSaveAttendance}
        saving={saving}
      />

      {isCheckerMode && (
        <div className="bg-white rounded-lg shadow border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-900 mb-3">Pending Attendance Corrections</h3>
          {correctionsLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : pendingCorrections.length === 0 ? (
            <p className="text-sm text-gray-500">No pending correction requests.</p>
          ) : (
            <div className="space-y-3">
              {pendingCorrections.map((item) => (
                <div key={item.id} className="border border-gray-200 rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <p className="font-medium text-gray-900">{item.employee_name} — {item.attendance_date}</p>
                    <p className="text-sm text-gray-600">{item.request_type}: {item.reason}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {item.original_status} → {item.requested_status || 'unchanged'}
                      {item.is_post_payroll_adjustment ? ' (post-payroll)' : ''}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => handleCorrectionAction(item.id, 'approve')} className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm">Approve</button>
                    <button type="button" onClick={() => handleCorrectionAction(item.id, 'reject')} className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm">Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <CorrectionModal
        isOpen={showCorrectionModal}
        record={correctionRecord}
        onClose={() => setShowCorrectionModal(false)}
        onSubmit={submitCorrection}
        saving={saving}
      />

      <HistoryModal
        employee={historyEmployee}
        history={history}
        loading={historyLoading}
        onClose={() => {
          setHistoryEmployee(null);
          setHistory(null);
        }}
        page={historyPage}
        setPage={setHistoryPage}
        historyFilters={historyFilters}
        setHistoryFilters={setHistoryFilters}
      />
    </div>
  );
};

export default AttendanceManagement;
